import React, {
  FunctionComponent,
  useDeferredValue,
  useMemo,
  useState,
} from 'react';
import MancoStaticExposureTable from './subComponents/MancoStaticExposureTable.component';
import MancoAssetGrossExposure from './subComponents/MancoAssetGrossExposure.component';
import MancoExposureBarChart from './subComponents/MancoExposureBarChart.component';
import GridItem from '../../../layout/GridComponents/GridItem';
import MancoCountryExposureBarChart from './subComponents/MancoCountryExposureBarChart.component';
import { FormControlLabel, Switch } from '@mui/material';
import UltraTable, {
  TableData,
  UltraTablePresetConfig,
} from '../../../tables/ultraTable/UltraTable';
import { ALL_EXPOSURE_COLUMNS } from '../../../tables/ultraTable/data/presetColumnData';
import { ultraTableStyles } from '../../../tables/ultraTable/styles/ultratable.styles';
import {
  buildColumns,
  buildData,
} from '../../../tables/ultraTable/utils/buildUltraTableData';
import {
  ALL_EXPOSURE_PRESETS,
  GeneralPreset,
} from '../../../tables/ultraTable/data/ultrapresetColumnData';
import UnderlyingPositionTable from '../../sharedComponents/UnderlyingPositionTable';
import { Position } from '../../../tables/ultraTable/types/position.types';
import { UltraTablePreset } from '../../../tables/ultraTable/types/presets.types';
import { formatDateForCheckingState } from '../../../../utilities/dateFormatters';
import { UltraTableWrapperProps } from '../../sharedComponents/ExposureFund';
import { EditableFields } from '../../../tables/ultraTable/types/editable.types';
import {
  useMancoExposurePageModel,
  type MancoExposurePayload,
} from '../../../../api/rs-hg/manco';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { getEntityLabelForSection } from '../../../../utilities/entityLabel';

const MancoExposure: React.FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
  section,
}) => {
  const classes = ultraTableStyles();
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const entityLabel = getEntityLabelForSection(section);

  const exposureQuery = useMancoExposurePageModel(
    positionDate,
    raptorPlatformId,
  );

  return (
    <QueryBoundary<MancoExposurePayload>
      isLoading={exposureQuery.isLoading}
      isFetching={exposureQuery.isFetching}
      isError={exposureQuery.isError}
      error={exposureQuery.error}
      data={exposureQuery.data?.data}
      loadingMessages={[
        `Loading ${entityLabel} Exposure Data...`,
        'This may take a few seconds...',
      ]}
      errorMessage="Error Retrieving Data"
      noDataMessage={`No data for ${positionDate}`}
    >
      {(payload) => {
        const selectedPositionDate =
          payload.selected_position_date ??
          exposureQuery.data?.selected_position_date ??
          positionDate;

        const viewData: MancoExposurePayload = {
          ...payload,
          selected_position_date: selectedPositionDate,
        };

        return (
          <>
            <GridItem
              xs={5}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <MancoAssetGrossExposure
                data={viewData}
                isFetching={exposureQuery.isFetching}
              />
              <MancoStaticExposureTable
                data={viewData}
                isFetching={exposureQuery.isFetching}
              />
            </GridItem>
            <MancoExposureBarChart
              data={viewData}
              isFetching={exposureQuery.isFetching}
            />
            <MancoCountryExposureBarChart
              data={viewData}
              isFetching={exposureQuery.isFetching}
            />

            <HedgePositionsTableWrapper
              originalData={viewData}
              isFetching={exposureQuery.isFetching}
              fundId={entityLabel.toLowerCase()}
              fundName={entityLabel.toLowerCase()}
              classes={classes}
            />
          </>
        );
      }}
    </QueryBoundary>
  );
};

export default MancoExposure;

//This is only here to deal with the render more hooks issue due to the buildDataFn having a context selector hook
const HedgePositionsTableWrapper: FunctionComponent<UltraTableWrapperProps> = ({
  originalData,
  fundId,
  fundName,
  classes,
  isFetching,
}) => {
  const [tableAggregate, setTableAggregate] = useState(true);
  // Defer the heavy rebuild so the switch stays responsive on large datasets
  const deferredAggregate = useDeferredValue(tableAggregate);
  const isAggregatePending = tableAggregate !== deferredAggregate;

  const data = useMemo(
    () => buildData(originalData, deferredAggregate),
    [originalData, deferredAggregate],
  );

  const columns = useMemo(
    () => buildColumns(deferredAggregate, ALL_EXPOSURE_COLUMNS, classes),
    [deferredAggregate, classes],
  );

  const tableData: TableData<EditablePosition> = useMemo(
    () => ({ columns, data }),
    [columns, data],
  );

  const totalAssetClasses = useMemo(() => {
    const assetClasses = new Set<string>();
    for (const item of data) {
      if (item?.asset_class) {
        assetClasses.add(item.asset_class);
      }
    }
    return assetClasses;
  }, [data]);

  const ultraTablePresets: UltraTablePresetConfig = useMemo(() => {
    const presets: UltraTablePreset[] = [];
    totalAssetClasses.forEach((asset) => {
      const preset = ALL_EXPOSURE_PRESETS.find((p) => p.name === asset);
      if (preset) {
        presets.push(preset);
      }
    });

    return {
      default: GeneralPreset,
      allPresets: presets,
      display: 'buttons',
    };
  }, [totalAssetClasses]);

  const tablePositionDate = originalData.selected_position_date;

  return (
    <UltraTable<EditablePosition>
      isFetching={isFetching}
      isAggregatePending={isAggregatePending}
      title="Exposure Data"
      tableData={tableData}
      getRowCanExpand={() => true}
      pdfConfig={{
        pdfIdentifier: `${fundId}_exposure_main`,
        pdfTitle: `Exposure Data - ${fundName}`,
        pdfExportGroupName: 'exposure_page',
        pdfExportGroupOrder: 5,
        pdfFileName: `exposure-${fundId}-${
          tablePositionDate || formatDateForCheckingState(new Date())
        }`,
      }}
      actionBtns={[
        <FormControlLabel
          control={
            <Switch
              checked={tableAggregate}
              onChange={() => setTableAggregate((prev) => !prev)}
              className={classes.switch}
            />
          }
          label={'Aggregate Table'}
          className={classes.switchContainer}
        />,
      ]}
      columnSelector
      positionDate={tablePositionDate}
      nestedComponent={UnderlyingPositionTable}
      presets={ultraTablePresets}
      resetExpansion={deferredAggregate}
    />
  );
};

export interface EditablePosition extends Position, EditableFields {}
